/**
 * Domain-agnostic SDK wrapper + TanStack Query factory for the
 * workspace-scoped artifacts list endpoint.
 *
 * Lifted from `features/artifacts/api/artifacts.api.ts` so any surface that
 * needs to read workspace artifacts (the `artifacts` feature itself,
 * `brief-generation` intake, etc.) can consume it without crossing into
 * the `artifacts` feature slice.
 *
 * The original feature path re-exports these symbols for backward
 * compatibility — existing consumers keep working unchanged.
 */
import { inferenceHttp } from '@/lib/api/http-client';
import { ArtifactsService } from '@inference.engine/api-client/web';
import type {
  ArtifactListResponseDto,
  GetApiV1WorkspacesByWorkspaceIdArtifactsData,
} from '@inference.engine/api-client/web';
import { infiniteQueryOptions } from '@tanstack/react-query';

import { WORKSPACE_ARTIFACTS_QUERY_KEYS, type WorkspaceArtifactsQueryParams } from './query-keys';

export const listWorkspaceArtifacts = async (
  workspaceId: string,
  query?: GetApiV1WorkspacesByWorkspaceIdArtifactsData['query'],
): Promise<ArtifactListResponseDto> => {
  const response = await ArtifactsService.listForWorkspace({
    client: inferenceHttp,
    path: { workspaceId },
    query,
  });

  if (!response.data) {
    throw new Error('Failed to list workspace artifacts');
  }

  return response.data;
};

export const WORKSPACE_ARTIFACTS_PAGE_SIZE = 100;

export const workspaceArtifactsInfiniteQuery = (
  workspaceId: string | undefined,
  params: WorkspaceArtifactsQueryParams = {},
) =>
  infiniteQueryOptions({
    queryKey: WORKSPACE_ARTIFACTS_QUERY_KEYS.list(workspaceId!, params),
    queryFn: ({ pageParam }) =>
      listWorkspaceArtifacts(workspaceId!, {
        skip: pageParam,
        take: WORKSPACE_ARTIFACTS_PAGE_SIZE,
        kind: params.kind ? [params.kind] : undefined,
        search: params.search,
        sortBy: params.sortBy,
        sortOrder: params.sortOrder,
        isUserInput: params.isUserInput,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const fetched = allPages.reduce((sum, page) => sum + page.artifacts.length, 0);
      return fetched < lastPage.total ? fetched : undefined;
    },
    enabled: !!workspaceId,
    // Picker workflow is "show me what I just generated" — global 5min staleTime
    // would mask an artifact created in another tab seconds ago. Always refetch
    // on mount so the picker reflects truth at the moment of opening.
    staleTime: 0,
    refetchOnMount: 'always',
  });
